"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createPractice,
  getAdminPracticeDetail,
  getPracticeDetail,
  getPublicProfile,
  invitePracticeMember,
  listAdminPractices,
  listMyPractices,
  listPracticeMembers,
  listPracticeServiceRequests,
  listProfilePractices,
  removePracticeMember,
  requestPracticeJoin,
  respondPracticeInvite,
  reviewPracticeJoin,
  searchPracticeInviteCandidates,
  searchPractices,
  setPracticeArchived,
  setPrimaryPractice,
  updatePractice,
  updatePracticeMember,
} from "@/lib/practices/api";
import type {
  PracticeAccessRole,
  PracticeCreateInput,
  PracticeProfessionalRole,
  PracticeUpdateInput,
} from "@/lib/practices/types";
import { queryKeys } from "@/lib/query-keys";
import { MissingSupabaseConfigError } from "@/lib/supabase/env";

function retryUnlessMissingConfig(failureCount: number, error: unknown) {
  return error instanceof MissingSupabaseConfigError ? false : failureCount < 1;
}

async function invalidatePracticeQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  practiceId?: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.myPractices }),
    queryClient.invalidateQueries({ queryKey: queryKeys.adminPractices }),
    practiceId
      ? queryClient.invalidateQueries({ queryKey: queryKeys.practice(practiceId) })
      : Promise.resolve(),
    practiceId
      ? queryClient.invalidateQueries({
          queryKey: queryKeys.practiceMembers(practiceId),
        })
      : Promise.resolve(),
    practiceId
      ? queryClient.invalidateQueries({
          queryKey: queryKeys.practiceServices(practiceId),
        })
      : Promise.resolve(),
    practiceId
      ? queryClient.invalidateQueries({
          queryKey: queryKeys.adminPractice(practiceId),
        })
      : Promise.resolve(),
  ]);
}

export function useMyPracticeMemberships(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.myPractices,
    queryFn: listMyPractices,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function usePractice(practiceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.practice(practiceId),
    queryFn: () => getPracticeDetail(practiceId),
    enabled: enabled && Boolean(practiceId),
    retry: retryUnlessMissingConfig,
  });
}

export function usePracticeMembers(practiceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.practiceMembers(practiceId),
    queryFn: () => listPracticeMembers(practiceId),
    enabled: enabled && Boolean(practiceId),
    retry: retryUnlessMissingConfig,
  });
}

export function usePracticeServices(practiceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.practiceServices(practiceId),
    queryFn: () => listPracticeServiceRequests(practiceId),
    enabled: enabled && Boolean(practiceId),
    retry: retryUnlessMissingConfig,
  });
}

export function usePracticeSearch(query: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.practiceSearch(query),
    queryFn: () => searchPractices(query),
    enabled: enabled && query.trim().length >= 2,
    retry: retryUnlessMissingConfig,
  });
}

export function usePracticeInviteCandidates(
  practiceId: string,
  query: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.practiceInviteCandidates(practiceId, query),
    queryFn: () => searchPracticeInviteCandidates(practiceId, query),
    enabled: enabled && query.trim().length >= 2,
    retry: retryUnlessMissingConfig,
  });
}

export function useProfilePractices(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.profilePractices(userId),
    queryFn: () => listProfilePractices(userId),
    enabled: enabled && Boolean(userId),
    retry: retryUnlessMissingConfig,
  });
}

export function usePublicProfile(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.publicProfile(userId),
    queryFn: () => getPublicProfile(userId),
    enabled: enabled && Boolean(userId),
    retry: retryUnlessMissingConfig,
  });
}

export function useAdminPractices(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminPractices,
    queryFn: listAdminPractices,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useAdminPractice(practiceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminPractice(practiceId),
    queryFn: () => getAdminPracticeDetail(practiceId),
    enabled: enabled && Boolean(practiceId),
    retry: retryUnlessMissingConfig,
  });
}

export function useCreatePractice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: PracticeCreateInput) => createPractice(input),
    onSuccess: async () => {
      await invalidatePracticeQueries(queryClient);
    },
  });
}

export function useUpdatePractice(practiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: PracticeUpdateInput) =>
      updatePractice(practiceId, input),
    onSuccess: async () => {
      await invalidatePracticeQueries(queryClient, practiceId);
    },
  });
}

export function useSetPracticeArchived(practiceId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { practiceId?: string; archived: boolean }) =>
      setPracticeArchived(input.practiceId ?? practiceId ?? "", input.archived),
    onSuccess: async (_data, variables) => {
      await invalidatePracticeQueries(
        queryClient,
        variables.practiceId ?? practiceId,
      );
    },
  });
}

export function useInvitePracticeMember(practiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      userId: string;
      professionalRole: PracticeProfessionalRole;
    }) => invitePracticeMember(practiceId, input.userId, input.professionalRole),
    onSuccess: async () => {
      await invalidatePracticeQueries(queryClient, practiceId);
    },
  });
}

export function useRespondPracticeInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { practiceId: string; accept: boolean }) =>
      respondPracticeInvite(input.practiceId, input.accept),
    onSuccess: async (_data, variables) => {
      await invalidatePracticeQueries(queryClient, variables.practiceId);
    },
  });
}

export function useRequestPracticeJoin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      practiceId: string;
      professionalRole: PracticeProfessionalRole;
    }) => requestPracticeJoin(input.practiceId, input.professionalRole),
    onSuccess: async (_data, variables) => {
      await invalidatePracticeQueries(queryClient, variables.practiceId);
    },
  });
}

export function useReviewPracticeJoin(practiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { userId: string; approve: boolean }) =>
      reviewPracticeJoin(practiceId, input.userId, input.approve),
    onSuccess: async () => {
      await invalidatePracticeQueries(queryClient, practiceId);
    },
  });
}

export function useUpdatePracticeMember(practiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      userId: string;
      professionalRole: PracticeProfessionalRole;
      accessRole: PracticeAccessRole;
    }) =>
      updatePracticeMember(
        practiceId,
        input.userId,
        input.professionalRole,
        input.accessRole,
      ),
    onSuccess: async () => {
      await invalidatePracticeQueries(queryClient, practiceId);
    },
  });
}

export function useRemovePracticeMember(practiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removePracticeMember(practiceId, userId),
    onSuccess: async () => {
      await invalidatePracticeQueries(queryClient, practiceId);
    },
  });
}

export function useSetPrimaryPractice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (practiceId: string) => setPrimaryPractice(practiceId),
    onSuccess: async (_data, practiceId) => {
      await invalidatePracticeQueries(queryClient, practiceId);
    },
  });
}
